import React, { useState } from 'react';

interface TimePickerProps {
  value: string;
  onChange: (time: string) => void;
  placeholder?: string;
  className?: string;
}

const TimePicker: React.FC<TimePickerProps> = ({ 
  value, 
  onChange, 
  placeholder = "Select time",
  className = "" 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hourInput, setHourInput] = useState('');
  const [minuteInput, setMinuteInput] = useState('');
  const [isAM, setIsAM] = useState(true);

  const formatDisplayTime = (timeString: string) => {
    if (!timeString) return '';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Initialize state from value
  React.useEffect(() => {
    if (value) {
      const [hours, minutes] = value.split(':').map(Number);
      // Convert 24-hour format to 12-hour format for display
      let displayHour = hours;
      let isAMValue = true;
      
      if (hours === 0) {
        displayHour = 12; // 12 AM (midnight)
        isAMValue = true;
      } else if (hours < 12) {
        displayHour = hours; // 1-11 AM
        isAMValue = true;
      } else if (hours === 12) {
        displayHour = 12; // 12 PM (noon)
        isAMValue = false;
      } else {
        displayHour = hours - 12; // 1-11 PM
        isAMValue = false;
      }
      
      setHourInput(displayHour.toString());
      setMinuteInput(minutes.toString().padStart(2, '0'));
      setIsAM(isAMValue);
    }
  }, [value]);

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = parseInt(value);
    
    // Allow empty, single digit, or valid hour (1-12)
    if (value === '' || (numValue >= 1 && numValue <= 12)) {
      setHourInput(value);
    }
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = parseInt(value);
    
    // Allow empty, single digit, or valid minute (0-59)
    if (value === '' || (numValue >= 0 && numValue <= 59)) {
      setMinuteInput(value);
    }
  };

  const handleAMPMToggle = (isAMValue: boolean) => {
    setIsAM(isAMValue);
  };

  const handleConfirm = () => {
    const hour = parseInt(hourInput);
    const minute = parseInt(minuteInput);
    
    // Validate inputs - hour must be 1-12, minute must be 0-59
    if (isNaN(hour) || hour < 1 || hour > 12 || isNaN(minute) || minute < 0 || minute > 59) {
      return; // Don't save if invalid
    }
    
    let finalHour = hour;
    if (!isAM && hour !== 12) finalHour += 12;
    if (isAM && hour === 12) finalHour = 0;
    
    const timeString = `${finalHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    onChange(timeString);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setIsOpen(false);
  };

  const handleNow = () => {
    const now = new Date();
    const timeString = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    onChange(timeString);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={formatDisplayTime(value)}
          onClick={() => setIsOpen(true)}
          readOnly
          placeholder={placeholder}
          className="w-full text-sm text-gray-900 bg-white border border-gray-200 rounded px-3 py-2 pr-8 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          <svg 
            className="w-4 h-4 text-gray-400 cursor-pointer" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            onClick={() => setIsOpen(true)}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>
      {isOpen && (
        <div className="absolute top-full left-0 z-[9999] mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-4 w-[240px] max-w-[calc(100vw-2rem)]">
          {/* Header */}
          <div className="text-center mb-3">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">SELECT TIME</h3>
            
            {/* Digital Time Input */}
            <div className="flex items-center justify-center space-x-1 mb-3">
              <input
                type="text"
                value={hourInput}
                onChange={handleHourChange}
                placeholder="12"
                maxLength={2}
                className="w-12 text-lg font-bold text-center border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-lg font-bold text-gray-700">:</span>
              <input
                type="text"
                value={minuteInput}
                onChange={handleMinuteChange}
                placeholder="00"
                maxLength={2}
                className="w-12 text-lg font-bold text-center border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              
              {/* AM/PM Selector */}
              <div className="flex flex-col ml-3">
                <button
                  onClick={() => handleAMPMToggle(true)}
                  className={`px-2 py-1 text-xs font-medium rounded-t ${
                    isAM 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  AM
                </button>
                <button
                  onClick={() => handleAMPMToggle(false)}
                  className={`px-2 py-1 text-xs font-medium rounded-b ${
                    !isAM 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  PM
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center">
            <button
              onClick={handleClear}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear
            </button>
            <div className="flex space-x-2">
              <button
                onClick={handleNow}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Now
              </button>
              <button
                onClick={handleConfirm}
                className="px-3 py-1 bg-blue-500 text-white text-xs font-medium rounded hover:bg-blue-600 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {isOpen && (
        <div 
          className="fixed inset-0 z-5" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default TimePicker; 
